import { useRef, useEffect, useState } from "react";

import { newRelic } from "utils/logUtils";

import usePrevious from "./usePrevious";

const useVideoJsPlayer = ({
  src,
  autoplay,
  onReady,
  onPlay,
  onPause,
  onTimeUpdate,
  onSeeking,
  onSeeked,
  onEnd,
  onError,
  className,
  ...props
}) => {
  const vjsId = `vjs-${Date.now()}`;
  const vjsRef = useRef(null);
  const videojs = useRef(null);

  const [isVideojsLoaded, setIsVideojsLoaded] = useState(false);
  let player;

  const previousSrc = usePrevious(src);
  const previousAutoplay = usePrevious(autoplay);

  const initPlayer = () => {
    player = videojs.current(vjsRef.current, props);
    player.src({
      src,
      type: "video/mp4",
    });
    player.autoplay(autoplay);
  };

  const changeSrc = () => {
    player = videojs.current(vjsRef.current);
    player.src({
      src,
      type: "video/mp4",
    });
  };

  const changeAutoplay = () => {
    player = videojs.current(vjsRef.current);
    player.autoplay(autoplay);

    if (autoplay) {
      player.play();
    } else {
      player.pause();
    }
  };

  const exitFullscreen = () => {
    player = videojs.current(vjsRef.current);

    if (player.isFullscreen()) {
      player.exitFullscreen();
    }
  };

  const resetPlayer = () => {
    player = videojs.current(vjsRef.current);
    player.currentTime(0);
    player.pause();
  };

  const initPlayerEvents = () => {
    let currentTime = 0;
    let previousTime = 0;
    let position = 0;

    player.ready(() => {
      if (onReady) {
        onReady(player);
      }
      window.p = player;
    });

    player.on("play", () => {
      if (onPlay) {
        onPlay(player.currentTime());
      }
    });

    player.on("pause", () => {
      if (onPause) {
        onPause(player.currentTime());
      }
    });

    player.on("timeupdate", () => {
      if (onTimeUpdate) {
        onTimeUpdate(player.currentTime());
        previousTime = currentTime;
        currentTime = player.currentTime();
        if (previousTime < currentTime) {
          position = previousTime;
          previousTime = currentTime;
        }
      }
    });

    player.on("seeking", () => {
      const completeTime = Math.floor(player.currentTime());
      onSeeked(position, completeTime);

      if (onSeeking) {
        player.off("timeupdate");
        player.one("seeked");
        onSeeking(player.currentTime());
      }
    });

    player.on("seeked", () => {
      if (onSeeked) {
        const completeTime = Math.floor(player.currentTime());
        onSeeked(position, completeTime);
      }
    });

    player.on("ended", () => {
      if (onEnd) {
        onEnd(player.currentTime());
      }
    });

    player.on("error", () => {
      const error = player.error();
      newRelic(`VideoJS Error: ${error.message}`, error);
      if (onError) {
        onError(error);
      }
    });
  };

  const unregisterEvents = () => {
    player.off("play");
    player.off("pause");
    player.off("timeupdate");
    player.off("seeking");
    player.off("seeked");
    player.off("ended");
  };

  useEffect(() => {
    import("video.js")
      .then((videojsPlayer) => {
        setIsVideojsLoaded(true);
        videojs.current = videojsPlayer.default;
      })
      .then(() => {
        if (previousSrc !== src && !previousSrc) {
          initPlayer();
          initPlayerEvents();
        }
        if (previousSrc !== src && previousSrc) {
          changeSrc();
          unregisterEvents();
          initPlayerEvents();
        }

        if (previousAutoplay !== autoplay && previousAutoplay !== undefined) {
          changeAutoplay();
          unregisterEvents();
          initPlayerEvents();
        }
      });
  }, [src, autoplay]);

  const videoClassNames = `video-js ${className} ${props.bigPlayButtonCentered ? "vjs-big-play-centered" : ""}`;

  return { vjsRef, vjsId, vjsClassName: videoClassNames, exitFullscreen, resetPlayer, isVideojsLoaded };
};

export default useVideoJsPlayer;
